import React from 'react';
import { Container, Typography, Box, Paper, Grid, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { keyframes } from '@mui/material/styles';
import SecurityIcon from '@mui/icons-material/Security';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SEO from './common/SEO';
import { colors } from '../theme';
import { color, r } from '../theme/tokens';
import { ScienceIcon, PersonalVideoIcon, AutoFixHighIcon } from '../icons';

// Animation keyframes
const fadeInUp = keyframes`
  from { 
    opacity: 0; 
    transform: translateY(2rem); 
  }
  to { 
    opacity: 1; 
    transform: translateY(0); 
  }
`;

const floatAnimation = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-0.5rem); }
`;

const AboutUs = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <>
      <SEO 
        title="About Us - myStack"
        description="Learn about myStack's mission to empower confident health decisions through science-based insights. Our story, philosophy, and vision for personalized wellness."
        keywords="about myStack, health technology, supplement recommendations, science-based wellness"
        canonicalUrl="/about-us"
      />
      
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${color.mint} 0%, ${color.evergreen} 100%)`,
          color: 'white',
          py: { xs: theme.spacing(8), md: theme.spacing(12) },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 30% 20%, rgba(255,255,255,0.1) 0%, transparent 50%)',
            pointerEvents: 'none',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            backgroundImage: `repeating-linear-gradient(0deg, rgba(255,255,255,0.03) 0, rgba(255,255,255,0.03) ${r(1)}, transparent ${r(1)}, transparent ${r(2)}), repeating-linear-gradient(90deg, rgba(255,255,255,0.02) 0, rgba(255,255,255,0.02) ${r(1)}, transparent ${r(1)}, transparent ${r(2)})`
            ,pointerEvents: 'none'
          }
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
            <Typography 
              variant="h1" 
              component="h1" 
              sx={{ 
                color: 'white',
                fontWeight: 700,
                mt: 0,
                mb: { xs: 3, md: 4 },
                fontSize: { xs: 'clamp(2.4rem, 4vw + 1rem, 3rem)', md: 'clamp(2.8rem, 4vw + 1rem, 3.8rem)' },
                letterSpacing: '-0.02em',
                lineHeight: 1.2,
                textShadow: `0 ${r(2)} ${r(8)} rgba(0,0,0,0.15), 0 ${r(1)} ${r(3)} rgba(255,255,255,0.1)`,
                position: 'relative',
              }}
            >
              About myStack
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'rgba(255,255,255,0.92)',
                fontSize: { xs: '1rem', md: '1.125rem' },
                fontWeight: 400,
                maxWidth: r(700),
                mx: 'auto',
                lineHeight: 1.6,
                letterSpacing: 0,
                animation: `${fadeInUp} 0.8s ease-out 0.2s both`
              }}
            >
              Cut through supplement confusion with science
            </Typography>
          </Box>
        </Container>
      </Box>

      <Box sx={{
        position: 'relative',
        background: 'linear-gradient(to right, transparent 0%, rgba(23, 79, 4, 0.08) 45%, rgba(23, 79, 4, 0.22) 100%)',
      }}>
      <Container maxWidth="lg" sx={{ py: theme.spacing(8) }}>
        {/* Content Sections: The Problem, Who We Are, Why We Built, Our Commitment */}
        <Box>
        <Box sx={{ pt: { xs: theme.spacing(2), md: theme.spacing(4) }, pb: { xs: theme.spacing(3), md: theme.spacing(5) } }}>
          <Typography 
            variant="h2"
            component="h2"
            sx={{ 
              fontWeight: 800,
              color: color.evergreen,
              textAlign: 'left',
              fontSize: { xs: '2.25rem', md: '3rem' },
              letterSpacing: '-0.02em',
              lineHeight: 1.1,
              mb: { xs: theme.spacing(1.5), md: theme.spacing(2.5) }
            }}
          >
            The Problem
          </Typography>
          <Typography 
            variant="body1"
            paragraph
            sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              maxWidth: '70ch',
              mr: 'auto',
              ml: 0,
              textAlign: 'left',
              mb: { xs: theme.spacing(1.5), md: theme.spacing(2.5) }
            }}
          >
            Like you, we stared at supplement bottles wondering: Does this even work? Is the dose right? Could it interact with something else I’m taking? Our friends and family were just as frustrated, trying to navigate influencer hype, sensational headlines, and conflicting product claims.
          </Typography>
        </Box>

        <Box sx={{ pt: { xs: theme.spacing(2), md: theme.spacing(4) }, pb: { xs: theme.spacing(3), md: theme.spacing(5) } }}>
          <Typography 
            variant="h2"
            component="h2"
            sx={{ 
              fontWeight: 800,
              color: color.evergreen,
              textAlign: 'left',
              fontSize: { xs: '2.25rem', md: '3rem' },
              letterSpacing: '-0.02em',
              lineHeight: 1.1,
              mb: { xs: theme.spacing(1.5), md: theme.spacing(2.5) }
            }}
          >
            Who We Are
          </Typography>
          <Typography 
            variant="body1"
            paragraph
            sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              maxWidth: '70ch',
              mr: 'auto',
              ml: 0,
              textAlign: 'left',
              mb: { xs: theme.spacing(1.5), md: theme.spacing(2.5) }
            }}
          >
            Tanu Bose, co-founder, is a pharmacist with a PhD in Nutrition and years of experience developing consumer health products. Aravind Hariramasamy, co-founder, is an engineer and product leader with deep expertise in AI products. Together, every time we wanted to take a supplement, the answers we needed were buried in research articles, not on the bottles.
          </Typography>
        </Box>

        <Box sx={{ pt: { xs: theme.spacing(2), md: theme.spacing(4) }, pb: { xs: theme.spacing(3), md: theme.spacing(5) } }}>
          <Typography 
            variant="h2"
            component="h2"
            sx={{ 
              fontWeight: 800,
              color: color.evergreen,
              textAlign: 'left',
              fontSize: { xs: '2.25rem', md: '3rem' },
              letterSpacing: '-0.02em',
              lineHeight: 1.1,
              mb: { xs: theme.spacing(1.5), md: theme.spacing(2.5) }
            }}
          >
            Why We Built myStack
          </Typography>
          <Typography 
            variant="body1"
            paragraph
            sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              maxWidth: '70ch',
              mr: 'auto',
              ml: 0,
              textAlign: 'left',
              mb: { xs: theme.spacing(1.5), md: theme.spacing(2.5) }
            }}
          >
            We left our jobs to build myStack and translate decades of research into clear, practical answers for your health goals. Our promise is simple — no guesswork, only transparent, personalized, evidence-based guidance you can trust.
          </Typography>
          <Typography 
            variant="body1"
            sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              maxWidth: '70ch',
              mr: 'auto',
              ml: 0,
              textAlign: 'left'
            }}
          >
            Our <strong>mission</strong> is to empower people to make confident health decisions with evidence, and our <strong>vision</strong> goes further — a world where every health choice is guided by science, not guesswork.
          </Typography>
        </Box>

        <Box sx={{ pt: { xs: theme.spacing(2), md: theme.spacing(4) }, pb: { xs: theme.spacing(3), md: theme.spacing(5) } }}>
          <Typography 
            variant="h2"
            component="h2"
            sx={{ 
              fontWeight: 800,
              color: color.evergreen,
              textAlign: 'left',
              fontSize: { xs: '2.25rem', md: '3rem' },
              letterSpacing: '-0.02em',
              lineHeight: 1.1,
              mb: { xs: theme.spacing(1.5), md: theme.spacing(2.5) }
            }}
          >
            Our Commitment
          </Typography>
          <Typography 
            variant="body1"
            paragraph
            sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              maxWidth: '70ch',
              mr: 'auto',
              ml: 0,
              textAlign: 'left'
            }}
          >
            Our recommendations are grounded in science, not sponsorship. Our story will evolve, but our commitment will not: to provide the clear guidance we once wished we had.
          </Typography>
        </Box>
        </Box>
        
      </Container>
      </Box>
    </>
  );
};

export default AboutUs;
